import React, { useEffect, useState } from "react";
import {
    Label,
    Input,
    Button,
    Field,
    Textarea,
    Text,
    Dropdown,
    Option
} from "@fluentui/react-components";
import { useTranslation } from "react-i18next";
import "../CSS/VariableForm.css";
const VariableForm = (props) => {
    const { t } = useTranslation();
    const [errors, setErrors] = useState({});
    const [dbJsonText, setDbJsonText] = useState("");

    useEffect(() => {
        if (props.variable?.DB) {
            setDbJsonText(JSON.stringify(props.variable.DB, null, 2));
        } else {
            setDbJsonText("");
        }
    }, [props.variable?.id]);

    useEffect(() => {
        if (props.variable) {
            validateForm();
        }
    }, [props.variable?.displayName, props.variable?.scope, props.variable?.DB]);

    // Fonction pour compter les clés d'un objet (non récursive)
    const countKeys = (obj) => {
        if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) {
            return 0;
        }
        return Object.keys(obj).length;
    };

    // Fonction pour vérifier la profondeur d'un objet
    // Compte uniquement les niveaux d'objets imbriqués (pas les arrays)
    const getObjectDepth = (obj, currentDepth = 1) => {
        if (typeof obj !== 'object' || obj === null) {
            return 0;
        }

        // Si c'est un array, ne pas compter comme un niveau mais vérifier le contenu
        if (Array.isArray(obj)) {
            for (const item of obj) {
                if (typeof item === 'object' && item !== null) {
                    // Arrays ne peuvent contenir que des primitives
                    return 999; // Force error
                }
            }
            return 0; // Array avec primitives ne compte pas comme niveau
        }

        // C'est un objet - ce niveau compte
        let maxDepth = currentDepth;
        for (const value of Object.values(obj)) {
            if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
                // Objet imbriqué - continuer récursivement
                const depth = getObjectDepth(value, currentDepth + 1);
                maxDepth = Math.max(maxDepth, depth);
            } else if (Array.isArray(value)) {
                // Vérifier que l'array ne contient que des primitives
                for (const item of value) {
                    if (typeof item === 'object' && item !== null) {
                        return 999; // Force error
                    }
                }
                // Array valide ne compte pas comme niveau supplémentaire
            }
        }

        return maxDepth;
    };

    const validateForm = () => {
        if (!props.variable) return;

        const newErrors = {};

        if (!props.variable.displayName)
            newErrors.displayName = t("THIS_FIELD_IS_REQUIRED");
        if (!props.variable.scope) newErrors.scope = t("THIS_FIELD_IS_REQUIRED");

        // Validation DB
        const db = props.variable.DB;
         if (!db) newErrors.db = t("THIS_FIELD_IS_REQUIRED");
        console.log("Validating DB:", db);
        if (db && typeof db === 'object') {
            // Vérifier la profondeur (max 2 niveaux)
            const depth = getObjectDepth(db);
            if (depth > 2) {
                newErrors.db = t("DB_MAX_2_LEVELS");
            }

            // Vérifier le nombre de clés au niveau racine de DB (max 100)
            const dbRootKeys = countKeys(db);
            if (dbRootKeys > 100) {
                newErrors.db = t("DB_MAX_100_KEYS");
            }

            // Vérifier que chaque objet au niveau 2 n'a pas plus de 100 clés
            for (const [key, value] of Object.entries(db)) {
                if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
                    const nestedKeys = countKeys(value);
                    if (nestedKeys > 100) {
                        newErrors.db = t("DB_MAX_100_KEYS");
                        break;
                    }
                }
            }

            // Vérifier que toutes les valeurs sont des primitives ou arrays de primitives au niveau 1
            // Les objets au niveau 2 ne peuvent contenir QUE des primitives
            const validateStructure = (obj, level = 1) => {
                for (const [key, value] of Object.entries(obj)) {
                    if (value === null) {
                        // null est accepté à tous les niveaux
                        continue;
                    }
                    
                    if (Array.isArray(value)) {
                        if (level === 1) {
                            // Arrays au niveau 1 : doivent contenir uniquement des primitives
                            for (const item of value) {
                                if (typeof item === 'object' && item !== null) {
                                    newErrors.db = t("CUSTOMDATA_ONLY_PRIMITIVES");
                                    return false;
                                }
                            }
                        } else {
                            // Arrays au niveau 2+ : PAS AUTORISÉS
                            newErrors.db = t("CUSTOMDATA_ONLY_PRIMITIVES");
                            return false;
                        }
                    } else if (typeof value === 'object') {
                        // Objet imbriqué
                        if (level === 1) {
                            // Au niveau 1, on peut avoir des objets, mais ils ne peuvent contenir que des primitives
                            const result = validateStructure(value, level + 1);
                            if (!result) return false;
                        } else {
                            // Au niveau 2+, pas d'objets imbriqués
                            newErrors.db = t("DB_MAX_2_LEVELS");
                            return false;
                        }
                    }
                    // Sinon c'est une primitive - OK à tous les niveaux
                }
                return true;
            };
            
            validateStructure(db);
        }

        setErrors(newErrors);

        const isValid = Object.keys(newErrors).length === 0;
        if (props.setFormValid) props.setFormValid(isValid);
    };

    const handleDbJsonChange = (e) => {
        const newJsonText = e.target.value;
        setDbJsonText(newJsonText);

        // Essayer de parser le JSON
        try {
            // Vérifier les clés dupliquées à TOUS les niveaux
            const findDuplicateKeys = (text) => {
                const allKeys = {}; // {depth: {key: count}}
                let depth = 0;
                let inString = false;
                let currentKey = '';
                let capturingKey = false;
                
                for (let i = 0; i < text.length; i++) {
                    const char = text[i];
                    const prevChar = i > 0 ? text[i - 1] : '';
                    
                    // Gérer les chaînes
                    if (char === '"' && prevChar !== '\\\\') {
                        if (!inString) {
                            inString = true;
                            if (depth > 0) {
                                capturingKey = true;
                                currentKey = '';
                            }
                        } else {
                            inString = false;
                            if (capturingKey && depth > 0) {
                                // Vérifier si c'est suivi de ":"
                                let j = i + 1;
                                while (j < text.length && /\\s/.test(text[j])) j++;
                                if (text[j] === ':') {
                                    // Initialiser le niveau si nécessaire
                                    if (!allKeys[depth]) {
                                        allKeys[depth] = {};
                                    }
                                    // Compter les occurrences
                                    if (!allKeys[depth][currentKey]) {
                                        allKeys[depth][currentKey] = 1;
                                    } else {
                                        allKeys[depth][currentKey]++;
                                    }
                                }
                                capturingKey = false;
                            }
                        }
                        continue;
                    }
                    
                    if (inString) {
                        if (capturingKey) {
                            currentKey += char;
                        }
                        continue;
                    }
                    
                    // Compter la profondeur
                    if (char === '{') depth++;
                    else if (char === '}') depth--;
                }
                
                // Trouver les duplicates à tous les niveaux
                const duplicates = [];
                for (const depthLevel in allKeys) {
                    for (const key in allKeys[depthLevel]) {
                        if (allKeys[depthLevel][key] > 1) {
                            duplicates.push(key);
                        }
                    }
                }
                
                return duplicates;
            };
            
            const duplicates = findDuplicateKeys(newJsonText);
            if (duplicates.length > 0) {
                setErrors(prev => ({ ...prev, db: t("DUPLICATE_KEYS_ERROR") + ": " + duplicates.join(", ") }));
                if (props.setFormValid) props.setFormValid(false);
                return;
            }

            const parsed = JSON.parse(newJsonText);

            // Vérifier que c'est un objet
            if (typeof parsed !== 'object' || Array.isArray(parsed)) {
                setErrors(prev => ({ ...prev, db: t("DB_MUST_BE_OBJECT") }));
                if (props.setFormValid) props.setFormValid(false);
                return;
            }

            // Vérifier que DB est un objet valide
            if (Array.isArray(parsed)) {
                setErrors(prev => ({ ...prev, db: t("DB_MUST_BE_OBJECT") }));
                if (props.setFormValid) props.setFormValid(false);
                return;
            }

            // Mettre à jour la variable
            props.setVariable((prev) => ({
                ...prev,
                DB: parsed,
            }));

            // Effacer l'erreur DB pour laisser validateForm faire la validation complète
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.db;
                return newErrors;
            });

            // La validation complète se fera dans useEffect
        } catch (error) {
            setErrors(prev => ({ ...prev, db: t("INVALID_JSON") }));
            if (props.setFormValid) props.setFormValid(false);
        }
    };
    return (
        <div className="variable-form-container">
            <div className="form-section-variable">


                <Field
                    label={t("DISPLAY_NAME")}
                    required
                    fontweight="semibold"
                    validationMessage={errors.displayName}
                    validationState={errors.displayName ? "error" : undefined}
                >
                    <Input
                        className="variable-form"
                        value={props.variable?.displayName || ""}
                        placeholder={t("ENTER_DISPLAY_NAME")}
                        onChange={(e) =>
                            props.setVariable((prev) => ({
                                ...prev,
                                displayName: e.target.value,
                            }))
                        }
                    />
                </Field>

                <Field
                    label={t("SCOPE")}
                    required
                    fontweight="semibold"
                    validationMessage={errors.scope}
                    validationState={errors.scope ? "error" : undefined}
                >
                    <Dropdown
                        className=" variable-form"
                        placeholder={t("SELECT_ENTITY")} 
                        value={props.variable?.scope || ""}
                        selectedOptions={[props.variable?.scope || ""]}
                        onOptionSelect={(event, data) =>
                            props.setVariable((prev) => ({
                                ...prev,
                                scope: data.optionValue,
                            }))
                        }
                    >
                        {props.SCOPES_NAMES?.map((scope) => (
                            <Option key={scope} value={scope}>
                                {scope}
                            </Option>
                        ))}
                    </Dropdown>
                </Field>
            </div>

            <div className="form-section-variable">
                

                <Field
                    label={t("DB_JSON")}
                    required
                    fontweight="semibold"
                    validationMessage={errors.db}
                    validationState={errors.db ? "error" : undefined}
                >
                    <Textarea
                        value={dbJsonText}
                        onChange={handleDbJsonChange}
                        rows={15}
                        resize="vertical"
                        className="db-json-textarea variable-form"
                        placeholder={`{\n  "key1": "value1",\n  "data": {\n    "name": "John",\n    "age": 30\n  }\n}`}
                    />
                </Field>

                <div className="validation-info">
                    <Text size={200} className="validation-info-text">
                        ✓ {t("DB_MAX_2_LEVELS_INFO")}<br />
                        ✓ {t("DB_MAX_100_KEYS_INFO")}<br />
                        ✓ {t("DB_PRIMITIVES_INFO")}
                    </Text>
                </div>
            </div>
        </div>
    );
};

export default VariableForm;
